// Qué puede elegir el usuario POR SU NOMBRE en cada canal, y qué no.
//
// Esto existe porque el conocimiento "Meta no publica los eventos personalizados del píxel uno por
// uno" no vivía en ninguna parte del sistema: estaba en la cabeza de quien investigó el caso, y la
// pantalla no lo decía. La primera versión de este arreglo lo iba a escribir hardcodeado adentro
// del selector; eso resuelve Meta y deja el mismo agujero abierto para el canal que se integre
// después. Acá es un dato por canal, así que la pregunta "¿qué no se puede medir en este canal?"
// pasa a ser parte de integrar uno nuevo.
//
// ⚠️ Qué superficie alcanza a cada canal, porque NO son las tres para todos:
//   - el estado vacío (sin resultados, y la cuenta sin eventos) → los cinco canales;
//   - la línea fija del pie → sólo los que declaran `limitationKey` (hoy Meta, Google, TikTok,
//     DV360): sin límite que avisar no hay línea que poner;
//   - el grupo de eventos propios con su instructivo → **sólo Meta**, porque es lo único que se
//     dibuja en la vista categorizada; el resto usa la lista plana y no tiene grupos.
// La primera versión de este comentario prometía paridad en los cinco y el código la daba en uno.
//
// Vive al lado del selector y no en `channels.js` a propósito: es conocimiento de ESTA pantalla,
// y `channels.js` lo importan hasta los helpers de texto del panel, que se mantienen livianos.

import {
    ANALYTICS_CHANNEL,
    DV360_CHANNEL,
    FACEBOOK_CHANNEL,
    GOOGLE_CHANNEL,
    LINKEDIN_CHANNEL,
    SHEET_CHANNEL,
    TIKTOK_CHANNEL,
} from '../channels/channels';
import {HELP_FACEBOOK_FIXED_ACTIONS, HELP_GA4} from '../help_center/LinkToArticle';

/**
 * Por canal:
 *
 *   canPickOwn      si se pueden elegir eventos propios del anunciante, por su nombre.
 *   objectKey       cómo llama LA PLATAFORMA a ese objeto (el nombre que el usuario va a buscar
 *                   cuando vaya a crearlo allá). i18n.
 *   limitationKey   qué NO se puede desglosar en este canal. `null` si no hay límite que avisar.
 *   howToKey        qué tiene que hacer el usuario para conseguirlo. i18n.
 *   aggregateActionId  el renglón que suma lo que no se desglosa, cuando existe: es la salida
 *                   inmediata, sin tocar nada en la plataforma.
 *   notFoundKey     (opcional) título del aviso cuando se repite dentro del grupo de eventos
 *                   propios ya poblado. i18n.
 *   helpArticle     constante de `LinkToArticle` con el artículo del canal.
 */
const OWN_EVENTS_BY_CHANNEL = {
    [FACEBOOK_CHANNEL]: {
        canPickOwn: true,
        objectKey: 'channel_own_events.facebook.object',
        limitationKey: 'channel_own_events.facebook.limitation',
        howToKey: 'channel_own_events.facebook.how_to',
        aggregateActionId: 'offsite_conversion.fb_pixel_custom',
        // El renglón agregado tiene su propio grupo, al final de la lista: así la explicación de
        // por qué Meta no los desglosa vive PEGADA al único renglón que los representa, en vez de
        // quedar suelta al pie de la pantalla.
        aggregateGroupKey: 'channel_own_events.facebook.aggregate_group',
        // La pregunta que encabeza el mismo aviso dentro de «Conversiones personalizadas»: quien
        // busca ahí su evento del píxel y no lo ve no sabe que son dos objetos distintos de Meta.
        notFoundKey: 'channel_own_events.facebook.not_found',
        helpArticle: HELP_FACEBOOK_FIXED_ACTIONS,
    },
    [GOOGLE_CHANNEL]: {
        canPickOwn: true,
        objectKey: 'channel_own_events.google.object',
        limitationKey: 'channel_own_events.google.limitation',
        howToKey: 'channel_own_events.google.how_to',
        aggregateActionId: null,
        helpArticle: null,
    },
    [ANALYTICS_CHANNEL]: {
        canPickOwn: true,
        objectKey: 'channel_own_events.analytics.object',
        limitationKey: null,
        howToKey: 'channel_own_events.analytics.how_to',
        aggregateActionId: null,
        helpArticle: HELP_GA4,
    },
    [LINKEDIN_CHANNEL]: {
        canPickOwn: true,
        objectKey: 'channel_own_events.linkedin.object',
        limitationKey: null,
        howToKey: 'channel_own_events.linkedin.how_to',
        aggregateActionId: null,
        helpArticle: null,
    },
    [TIKTOK_CHANNEL]: {
        canPickOwn: false,
        objectKey: null,
        limitationKey: 'channel_own_events.tiktok.limitation',
        howToKey: null,
        aggregateActionId: null,
        helpArticle: null,
    },
    [DV360_CHANNEL]: {
        canPickOwn: false,
        objectKey: null,
        limitationKey: 'channel_own_events.dv360.limitation',
        howToKey: null,
        aggregateActionId: null,
        helpArticle: null,
    },
    [SHEET_CHANNEL]: {
        canPickOwn: false,
        objectKey: null,
        limitationKey: null,
        howToKey: null,
        aggregateActionId: null,
        helpArticle: null,
    },
};

/**
 * Un canal que no está declarado no rompe nada: devuelve `null` y las superficies que consultan
 * esto simplemente no dibujan la ayuda. Es el default correcto — inventarle un texto a un canal
 * que nadie miró sería peor que el silencio.
 */
export function ownEventsInfoFor(channel) {
    return OWN_EVENTS_BY_CHANNEL[channel] || null;
}
